import { TextTooltip } from '@ui/components/TextTooltip';
import { DISCORD_LINK } from '@ui/constants/links';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { formatBigValue } from '@ui/lib/bignumber';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAccount, useReadContract, useReadContracts } from 'wagmi';
import SLVMONABI from '@ui/services/contract/abis/SLVMON';
import SLVMONLimiterABI from '@ui/services/contract/abis/SLVMONLimiter';
import { Address, parseUnits } from 'viem';
import { Box } from 'styled-system/jsx';

import { ExchangeContext } from '../Context';

export const LimiterWarning = () => {
  const {
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
    isQuoteLoading,
  } = useContext(ExchangeContext);
  const { t } = useTranslation();
  const { address } = useAccount();

  // 1. Get Limiter address from Output Token (assuming it is SLVMON)
  const { data: limiterAddress } = useReadContract({
    address: outputToken?.address as Address,
    abi: SLVMONABI,
    functionName: 'limiter',
    query: {
      enabled: !!outputToken?.address,
      staleTime: 86400
    },
  });

  // 2. Get User Balance and Total Assets from SLVMON
  const { data: slvmonData } = useReadContracts({
    contracts: [
      {
        address: outputToken?.address as Address,
        abi: SLVMONABI,
        functionName: 'balanceOf',
        args: [address as Address],
      },
      {
        address: outputToken?.address as Address,
        abi: SLVMONABI,
        functionName: 'totalAssets',
      },
    ],
    query: {
      enabled: !!limiterAddress && !!address,
    },
  });

  const userBalance = slvmonData?.[0]?.result;
  const totalAssets = slvmonData?.[1]?.result;

  // 3. Convert user balance to assets
  const { data: userCurrentAssets } = useReadContract({
    address: outputToken?.address as Address,
    abi: SLVMONABI,
    functionName: 'convertToAssets',
    args: [userBalance || 0n],
    query: {
      enabled: userBalance !== undefined,
    },
  });

  // 4. Check Deposit on Limiter
  const inputAmountBN = useMemo(() => {
    if (!inputAmount || !inputToken) return 0n;
    try {
      return parseUnits(inputAmount, inputToken.decimals);
    } catch {
      return 0n;
    }
  }, [inputAmount, inputToken]);

  const { data: checkResult } = useReadContract({
    address: limiterAddress,
    abi: SLVMONLimiterABI,
    functionName: 'checkDeposit',
    args: [
      address as Address,
      inputAmountBN,
      userCurrentAssets || 0n,
      totalAssets || 0n,
    ],
    query: {
      enabled: !!limiterAddress && !!address && userCurrentAssets !== undefined && totalAssets !== undefined && inputAmountBN > 0n,
    },
  });

  // 5. Get User Cap
  const { data: userCap } = useReadContract({
    address: limiterAddress,
    abi: SLVMONLimiterABI,
    functionName: 'userCap',
    args: [address as Address],
    query: {
      enabled: !!limiterAddress && !!address,
    },
  });

  const isExceeded = useMemo(() => {
    if (checkResult) {
      return checkResult[0] === false && checkResult[1].trim() === 'USER_CAP_EXCEEDED';
    }
    // Fallback check if checkResult is not available yet but we have amounts
    if (inputAmountBN > 0n && userCap !== undefined && userCurrentAssets !== undefined) {
      return (userCurrentAssets + inputAmountBN) > userCap;
    }
    return false;
  }, [checkResult, inputAmountBN, userCap, userCurrentAssets]);

  const capFormatted = formatBigValue(userCap || 0n, {
    decimals: outputToken?.decimals || 18,
    displayDecimals: 2,
  });

  const remainingBN = useMemo(() => {
    if (userCap === undefined || userCurrentAssets === undefined) return 0n;
    const rem = userCap - userCurrentAssets;
    return rem < 0n ? 0n : rem;
  }, [userCap, userCurrentAssets]);

  const remainingFormatted = formatBigValue(remainingBN, {
    decimals: outputToken?.decimals || 18,
    displayDecimals: 2,
  });

  const remainingAfterInputFormatted = useMemo(() => {
    const rem = remainingBN - inputAmountBN;
    if (rem < 0n) return '0.00';
    return formatBigValue(rem, {
      decimals: outputToken?.decimals || 18,
      displayDecimals: 2,
    });
  }, [remainingBN, inputAmountBN, outputToken]);

  if (userCap === undefined || userCurrentAssets === undefined) return null;

  let remainingText;
  if (inputAmountBN <= 0n) {
    remainingText = remainingFormatted;
  } else if (isExceeded) {
    remainingText = 'Exceeds';
  } else {
    remainingText = remainingAfterInputFormatted;
  }

  return (
    <Box w="full" py={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Text color="rgba(255,255,255,0.5)" fontSize={12} fontWeight={400}>
            Your Allocated Quota:
          </Text>
          <TextTooltip
            closeDelay={200}
            content={
              <Box display="flex" flexDirection="column" gap={2}>
                <Text color="rgba(255,255,255,0.8)" fontSize={12} lineHeight="18px">
                  Currently, LVMON staking is only open to:
                </Text>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text color="rgba(255,255,255,0.8)" fontSize={12} lineHeight="18px">
                    1. Active LeverUp Traders
                  </Text>
                  <Text color="rgba(255,255,255,0.8)" fontSize={12} lineHeight="18px">
                    2. $LV and $LVMON holders
                  </Text>
                </Box>
                <Text color="rgba(255,255,255,0.8)" fontSize={12} lineHeight="18px">
                  To get more LVMON Staking quota, join our{' '}
                  <Link
                    href={DISCORD_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="#D4F82D"
                    display="inline"
                    fontWeight={600}
                  >
                    Discord
                  </Link>{' '}
                  to learn the steps.
                </Text>
              </Box>
            }
            triggerCss={{
              textDecoration: 'none',
              cursor: 'help',
            }}
            contentCss={{
              w: '240px',
              lineHeight: '18px',
              whiteSpace: 'normal',
            }}
          >
            <Text
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="12px"
              h="12px"
              borderRadius="9999px"
              border="1px solid rgba(255,255,255,0.35)"
              color="rgba(255,255,255,0.55)"
              fontSize={9}
              lineHeight="9px"
            >
              ?
            </Text>
          </TextTooltip>
        </Box>
        <Box display="flex" alignItems="center" gap={1} whiteSpace="nowrap">
          <Text
            color={isExceeded ? 'red' : 'greyWhite.lightGrey1'}
            fontSize={12}
            fontWeight={400}
          >
            {capFormatted} LVMON
          </Text>
          <Text
            color={isExceeded ? 'red' : 'rgba(255,255,255,0.5)'}
            fontSize={12}
            fontWeight={400}
          >
            (Remaining:{remainingText})
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
